// The Zoo stage's Pixi side: habitat parallax behind, a row/grid of procedural creatures (one per
// sound slot, labelled with its sound name) and accent particles in front. All drawing is code.
import { Application, Container, Text } from "pixi.js";
// No runtime code generation, so the stage works under the production CSP (no unsafe-eval).
import "pixi.js/unsafe-eval";
import { STREAM_BPM as BPM } from "../stream";
import { liveHits } from "./bus";
import { ProceduralCreature } from "./creature/ProceduralCreature";
import { generateGenome } from "./creature/genome";
import { ParticleBurst } from "./creature/particles";
import { restPose, type CreaturePose } from "./creature/pose";
import type { PadNote } from "./creature/creatureState";
import { layoutCreatures } from "./layout";
import { advanceTransport, buildSlotNotes, newTransport, NUM_SLOTS, slotFrame, traitsForSlot } from "./director";
import { createHabitat, DEFAULT_VARIANT, type Habitat } from "./habitat";
import type { AudiblePosition } from "./lib/clock";
import { hashString } from "./lib/rng";
import { INK, SPECIES_COLORS, mix } from "./palette";
import type { ZooClock, ZooCreatureDef, ZooScene } from "./types";

export const SYSTEM_FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

interface Slot {
  def: ZooCreatureDef;
  root: Container;
  label: Text;
  creature: ProceduralCreature | null;
  key: string;
  pose: CreaturePose;
  lastBurst: number;
  ripple: number;
}

export class ZooPixiStage {
  readonly app = new Application();
  private habitatLayer = new Container();
  private creatureLayer = new Container();
  private particles = new ParticleBurst(240, 7);
  private habitat: Habitat | null = null;
  private habitatKind: ZooScene["habitat"] | null = null;
  private slots: Slot[] = [];
  private w = 800;
  private h = 300;
  private size = 48;
  private cellW = 80;
  private fontSize = 11;
  private calm = false;
  private last = performance.now();
  private transport = newTransport();
  private notes: PadNote[][] = buildSlotNotes([], 1);
  private notesFor: readonly unknown[] | null = null;
  private notesLen = -1;
  private resizeObs: ResizeObserver | null = null;
  private destroyed = false;
  private onPointer = (e: PointerEvent) => {
    if (this.calm) return;
    this.habitat?.setParallax((e.offsetX / this.w) * 2 - 1, (e.offsetY / this.h) * 2 - 1);
  };

  constructor(
    private scene: ZooScene,
    private clock: ZooClock,
    calm: boolean,
  ) {
    this.calm = calm;
  }

  async init(host: HTMLElement, label: string): Promise<void> {
    await this.app.init({
      resizeTo: host,
      antialias: true,
      backgroundAlpha: 0,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      autoDensity: true,
      preference: "webgl",
    });
    const canvas = this.app.canvas;
    canvas.style.display = "block";
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", label);
    host.appendChild(canvas);
    this.resizeObs = new ResizeObserver(() => this.app.queueResize());
    this.resizeObs.observe(host);
    canvas.addEventListener("pointermove", this.onPointer);
    this.app.stage.addChild(this.habitatLayer, this.creatureLayer, this.particles);
    this.app.renderer.on("resize", (w: number, h: number) => this.resize(w, h));
    this.w = this.app.screen.width;
    this.h = this.app.screen.height;
    this.setScene(this.scene);
    this.setCalm(this.calm);
    this.app.ticker.add(() => this.frame());
  }

  setLabel(label: string): void {
    this.app.canvas?.setAttribute("aria-label", label);
  }

  setClock(clock: ZooClock): void {
    this.clock = clock;
  }

  setCalm(calm: boolean): void {
    this.calm = calm;
    this.particles.setEnabled(!calm);
    this.habitat?.setMotion(calm, !calm);
    if (calm) this.habitat?.setParallax(0, 0);
  }

  setScene(scene: ZooScene): void {
    this.scene = scene;
    this.ensureHabitat(scene.habitat);
    const defs = scene.creatures.slice(0, NUM_SLOTS);
    while (this.slots.length > defs.length) {
      const s = this.slots.pop()!;
      s.root.destroy({ children: true });
    }
    while (this.slots.length < defs.length) this.slots.push(this.makeSlot(defs[this.slots.length]));
    defs.forEach((d, i) => (this.slots[i].def = d));
    this.layout();
  }

  private makeSlot(def: ZooCreatureDef): Slot {
    const root = new Container();
    const label = new Text({
      text: "",
      style: {
        fontFamily: SYSTEM_FONT,
        fontSize: 11,
        fontWeight: "700",
        fill: 0xfff6e0,
        stroke: { color: INK, width: 3, join: "round" },
      },
    });
    label.anchor.set(0.5, 0);
    root.addChild(label);
    this.creatureLayer.addChild(root);
    return { def, root, label, creature: null, key: "", pose: restPose(), lastBurst: 0, ripple: 0 };
  }

  private ensureHabitat(kind: ZooScene["habitat"]): void {
    if (kind === this.habitatKind && this.habitat) return;
    this.habitatKind = kind;
    if (this.habitat) {
      this.habitatLayer.removeChild(this.habitat.view);
      this.habitat.destroy();
    }
    this.habitat = createHabitat(kind, {
      width: this.w,
      height: this.h,
      seed: hashString(`zoo:${kind}`),
      variant: DEFAULT_VARIANT,
      reducedMotion: this.calm,
      particles: !this.calm,
    });
    this.habitatLayer.addChild(this.habitat.view);
  }

  private resize(w: number, h: number): void {
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    this.habitat?.resize(w, h);
    this.layout();
  }

  private layout(): void {
    const L = layoutCreatures(this.slots.length, this.w, this.h);
    this.size = L.size;
    this.cellW = L.cellW;
    this.fontSize = L.fontSize;
    const n = this.slots.length;
    // Back rows first so front creatures overlap them.
    const order = this.slots.map((s, i) => ({ s, y: L.cells[i].y }));
    order.sort((a, b) => a.y - b.y);
    order.forEach(({ s }, z) => this.creatureLayer.setChildIndex(s.root, z));
    this.slots.forEach((s, i) => {
      const c = L.cells[i];
      s.root.position.set(c.x, c.y);
      s.ripple = n > 1 ? c.x / Math.max(1, this.w) : 0;
      s.label.style.fontSize = this.fontSize;
      s.label.y = 4;
    });
  }

  private truncate(name: string): string {
    const maxChars = Math.max(4, Math.floor(this.cellW / (this.fontSize * 0.58)));
    return name.length > maxChars ? name.slice(0, maxChars - 1) + "…" : name;
  }

  private ensureCreature(s: Slot): void {
    const d = s.def;
    const key = `${d.species}:${d.seed}:${d.slot}:${Math.round(this.size)}`;
    const text = this.truncate(d.name);
    if (s.label.text !== text) s.label.text = text;
    if (key === s.key) return;
    s.key = key;
    if (s.creature) {
      s.root.removeChild(s.creature);
      s.creature.destroy();
    }
    s.creature = new ProceduralCreature(generateGenome(d.species, d.seed, traitsForSlot(d.species, d.slot)), {
      size: this.size,
    });
    s.root.addChildAt(s.creature, 0);
  }

  private frame(): void {
    if (this.destroyed) return;
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    const pos = this.clock.position();
    const len = Math.max(1, pos.lengthTicks);
    const events = this.clock.events();
    if (events !== this.notesFor || len !== this.notesLen) {
      this.notes = buildSlotNotes(events, len);
      this.notesFor = events;
      this.notesLen = len;
    }
    const audible: AudiblePosition = advanceTransport(this.transport, pos);
    const level = Math.max(0, Math.min(1, this.clock.level?.() ?? 0));
    const beats = audible.playing ? audible.transportTick / 960 : (now / 60000) * BPM;
    this.habitat?.update(dt, {
      masterPeak: level,
      masterRms: level * 0.4,
      beatPhase: ((beats % 1) + 1) % 1,
      barPhase: (((beats / 4) % 1) + 1) % 1,
      bpm: BPM,
      playing: audible.playing,
    });
    for (const s of this.slots) {
      this.ensureCreature(s);
      const slot = s.def.slot;
      const inRange = slot >= 0 && slot < NUM_SLOTS;
      const { hit, pose } = slotFrame(
        {
          nowMs: now,
          audible,
          lengthTicks: len,
          notes: inRange ? this.notes[slot] : [],
          liveHitAt: inRange ? liveHits.at[slot] : 0,
          liveHitVel: inRange ? liveHits.vel[slot] : 0,
          seed: s.def.seed,
          ripple: s.ripple,
          calm: this.calm,
        },
        s.pose,
      );
      s.creature?.update(pose, dt);
      if (pose.state === "accent" && !this.calm) {
        const startedAt = Math.round(now - hit.agoMs);
        if (Math.abs(startedAt - s.lastBurst) > 2) {
          s.lastBurst = startedAt;
          const color = SPECIES_COLORS[s.def.species][0];
          this.particles.burst(
            s.root.x,
            s.root.y - this.size * 0.7,
            mix(color, 0xffffff, 0.3),
            this.size < 40 ? 6 : 12,
            "star",
          );
        }
      }
    }
    this.particles.update(dt);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.resizeObs?.disconnect();
    this.app.canvas?.removeEventListener("pointermove", this.onPointer);
    this.habitat?.destroy();
    this.habitat = null;
    this.app.destroy(true, { children: true });
  }
}
